import { saveEntry as submitEntry } from '@mysimpledb/shared/data/submitEntry';
import { useNotify } from '@mysimpledb/shared/context/NotificationContext';
import {
  uploadAttachments,
  uploadSensitiveAttachments,
} from '../api/attachmentsClient';
import { encryptSensitiveAttachment } from './crypto/sensitiveAttachmentCrypto';
import {
  formatSkippedAttachmentMessage,
  partitionFilesByAllowedType,
} from './attachmentUploadTypes';

export function useSaveEntry() {
  const notify = useNotify();

  const saveEntry = async ({
    form,
    isPublic,
    isSensitive,
    keepSynced = false,
    pendingFiles,
    setCurrentEntryId,
    setIsEditing,
    setPendingFiles,
    token,
    sensitiveKey = null,
    userId,
    onSyncedAttachmentTracked,
  }) => {
    const wholeSaveStartedAt = Date.now();

    console.log('[upload-client] whole-save-start', {
      existingEntryId: form?.id || null,
      fileCount: pendingFiles.length,
      totalBytes: pendingFiles.reduce(
        (total, file) => total + Number(file.size || 0),
        0
      ),
      keepSynced,
    });

    try {
      let entryId = form?.id;

      const entry = {
        ...form,
        isPublic,
        isSensitive,
      };

      const entrySaveStartedAt = Date.now();

      entryId = await submitEntry({
        entry,
        token,
        entryId,
        sensitiveKey,
      });

      console.log('[upload-client] entry-api-save-complete', {
        entryId,
        ms: Date.now() - entrySaveStartedAt,
      });

      const { allowed: uploadableFiles, rejected: clientRejected } =
        partitionFilesByAllowedType(pendingFiles);
      const hasUploadableFiles = uploadableFiles.length > 0;
      let rejectedFiles = [...clientRejected];

      if (hasUploadableFiles) {
        let uploadNoticeTimer = null;
        let uploadNoticeVisible = false;

        uploadNoticeTimer = setTimeout(() => {
          uploadNoticeVisible = true;

          notify.show(
            `Uploading ${uploadableFiles.length} attachment${
              uploadableFiles.length === 1 ? '' : 's'
            }...\n\nPlease wait.`,
            60000
          );
        }, 750);

        const attachmentUploadStartedAt = Date.now();
        let uploadResult;

        try {
          if (isSensitive) {
            if (!sensitiveKey) {
              throw new Error(
                'Unlock the Sensitive Vault before uploading attachments'
              );
            }
            if (keepSynced) {
              throw new Error(
                'Keep Synced is unavailable for sensitive attachments'
              );
            }

            const encryptedItems = [];
            for (const file of uploadableFiles) {
              encryptedItems.push(
                await encryptSensitiveAttachment({
                  entryId,
                  file,
                  vaultKey: sensitiveKey,
                })
              );
            }

            uploadResult = await uploadSensitiveAttachments(
              entryId,
              encryptedItems,
              token
            );
          } else {
            uploadResult = await uploadAttachments(
              entryId,
              uploadableFiles,
              token
            );
          }
        } finally {
          clearTimeout(uploadNoticeTimer);
          if (uploadNoticeVisible) notify.hide();
        }

        const serverRejected = Array.isArray(uploadResult?.rejected)
          ? uploadResult.rejected.map((item) => ({
              name: item?.name,
              reason: item?.reason || 'Unsupported attachment type',
            }))
          : [];
        rejectedFiles = [...rejectedFiles, ...serverRejected];

        console.log('[upload-client] attachment-api-complete', {
          entryId,
          attachmentCount: uploadResult?.attachments?.length || 0,
          ms: Date.now() - attachmentUploadStartedAt,
          wholeSaveElapsedMs: Date.now() - wholeSaveStartedAt,
        });

        const uploadedAttachments = uploadResult?.attachments || [];
        const syncAllStartedAt = Date.now();

        if (
          keepSynced &&
          !isSensitive &&
          window.syncApi &&
          Array.isArray(uploadedAttachments)
        ) {
          for (let i = 0; i < uploadedAttachments.length; i++) {
            const syncOneStartedAt = Date.now();
            const uploadedAttachment = uploadedAttachments[i];
            const file = uploadableFiles[i];

            console.log('[upload-client] sync-attachment-start', {
              index: i,
              attachmentId: uploadedAttachment?.id || null,
              name: file?.uploadName || file?.name || null,
            });

            if (!uploadedAttachment?.id || !file) continue;

            const localPath =
              file.localPath || file.path || file.webkitRelativePath || null;

            if (!localPath) {
              console.warn(
                '[sync] no local path available for file:',
                file.uploadName || file.name
              );
              continue;
            }

            try {
              console.log('[sync-debug]', {
                fileName: file.uploadName || file.name,
                path: file.path,
                webkitRelativePath: file.webkitRelativePath,
                uploadedAttachment,
              });

              const trackStartedAt = Date.now();

              const tracked = await window.syncApi.trackAttachment({
                userId,
                attachmentId: uploadedAttachment.id,
                localPath,
                lastSyncedVersion: 1,
                lastSyncedHash: null,
                syncEnabled: true,
                lastSeenMtime: file.lastModified || null,
                lastSeenSize: file.size || null,
              });

              console.log('[upload-client] track-attachment-complete', {
                attachmentId: uploadedAttachment.id,
                ms: Date.now() - trackStartedAt,
              });

              const callbackStartedAt = Date.now();

              await onSyncedAttachmentTracked?.({
                attachmentId: uploadedAttachment.id,
                syncEnabled: true,
                syncStatus: 'synced',
                deviceId: tracked?.device_id || tracked?.deviceId || null,
                deviceName: tracked?.device_name || tracked?.deviceName || null,
                lastLocalModifiedAt: file.lastModified
                  ? new Date(file.lastModified).toISOString()
                  : new Date().toISOString(),
              });

              console.log('[upload-client] synced-callback-complete', {
                attachmentId: uploadedAttachment.id,
                ms: Date.now() - callbackStartedAt,
              });

              if (window.syncApi?.refreshWatcher) {
                const watcherStartedAt = Date.now();
                await window.syncApi.refreshWatcher(userId);

                console.log('[upload-client] refresh-watcher-complete', {
                  attachmentId: uploadedAttachment.id,
                  ms: Date.now() - watcherStartedAt,
                });
              }

              console.log('[upload-client] sync-attachment-complete', {
                attachmentId: uploadedAttachment.id,
                totalMs: Date.now() - syncOneStartedAt,
              });

              console.log(
                '[sync] tracking attachment:',
                uploadedAttachment.id,
                localPath
              );
            } catch (err) {
              console.error('[sync] failed to track attachment:', err);
            }
          }

          console.log('[upload-client] all-sync-complete', {
            attachmentCount: uploadedAttachments.length,
            ms: Date.now() - syncAllStartedAt,
          });
        } else {
          console.log('[upload-client] sync-skipped', {
            keepSynced,
            syncApiAvailable: !!window.syncApi,
            attachmentCount: uploadedAttachments.length,
            ms: Date.now() - syncAllStartedAt,
          });
        }
      }

      console.log('[upload-client] pre-final-ui-update', {
        ms: Date.now() - wholeSaveStartedAt,
      });

      const skippedMessage = formatSkippedAttachmentMessage(rejectedFiles);
      let finalMessage = hasUploadableFiles
        ? 'Entry and files saved!'
        : 'Entry has been saved!';

      if (skippedMessage) {
        finalMessage = hasUploadableFiles
          ? `${finalMessage}

${skippedMessage}`
          : `Entry saved without the unsupported attachment${
              rejectedFiles.length === 1 ? '' : 's'
            }.

${skippedMessage}`;
      }

      notify.show(finalMessage, skippedMessage ? 7000 : undefined);

      if (!form?.id) {
        setCurrentEntryId(entryId);
      }

      setPendingFiles([]);
      setIsEditing(false);

      console.log('[upload-client] whole-save-complete', {
        entryId,
        ms: Date.now() - wholeSaveStartedAt,
      });

      return entryId;
    } catch (err) {
      console.error('[upload-client] whole-save-failed', {
        ms: Date.now() - wholeSaveStartedAt,
        error: err,
      });
      console.error('Error submitting entry:', err);
      throw err;
    }
  };

  return saveEntry;
}
